import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useParams, Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  InfoIcon,
  HelpCircleIcon,
  CircleCheck,
  CircleX,
  Flag,
} from 'lucide-react';

import { useApi } from 'shared_components/context';
import { useWError } from 'shared_components/components/WError/WErrorProvider';
import {
  AdminPersonalInformation,
  PrimaryDiscographyRequestSortFieldsInner,
  PrimaryDiscographyRequestSortFieldsInnerFieldEnum,
  PrimaryDiscographyRequestFieldFiltersInner,
  ConfidenceStats,
  AvailableFiltersResponse,
  RecordingWestburyContributionsInner,
} from 'shared_components/generated/admin';
import { AgCheckboxFilter } from './Components/AgCheckboxFilter';
import { AgTextFilter } from './Components/AgTextFilter';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import { WError } from 'shared_components/components/WError/WError';
import PageLayout from './PageLayout';
import WModal from 'shared_components/components/WModal';
import ThankyouBlock from 'shared_components/components/ThankYouBlock/ThankyouBlock';
import {
  RecordingEditForm,
  type RecordingEditFormHandle,
} from './Components/RecordingEditForm';
import { VerificationChart } from './Components/ConfidenceAnalysisFilter';
import WDropdown from 'shared_components/components/WDropdownContent/WDropdown';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import type {
  ColDef,
  ColSpanParams,
  ColumnState,
  FilterChangedEvent,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  IHeaderParams,
  PostSortRowsParams,
  RowClassParams,
  RowHeightParams,
  RowStyle,
  SortChangedEvent,
  SortDirection,
  SortModelItem,
} from 'ag-grid-community';
import { themeQuartz } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import {
  ChevronRight,
  ChevronDown,
  X,
  ChevronsLeft,
  ChevronsRight,
  ArrowUp,
  ArrowDown,
  Eye,
  EyeOff,
  Filter,
} from 'lucide-react';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination';
import { ContributionPill } from './Components/ContributionPill';
import { allContributionRoles } from '../../data/contributionsTaxonomy';
import { allReleaseFormats } from '../../data/releaseFormatTaxonomy';
import { allReleaseTypes } from '../../data/releaseTypeTaxonomy';

// Register all Community features
ModuleRegistry.registerModules([AllCommunityModule]);

// to use myTheme in an application, pass it to the theme grid option
const myTheme = themeQuartz.withParams({
  backgroundColor: '#151719',
  browserColorScheme: 'dark',
  chromeBackgroundColor: '#151719',
  foregroundColor: '#FFFFFF',
  headerFontSize: 14,
  headerFontWeight: 'bold',
  headerRowBorder: false,
  wrapperBorder: false,
});

// Map ag-grid column IDs to API sort fields
const GRID_SORT_TO_API_FIELD: Partial<
  Record<string, PrimaryDiscographyRequestSortFieldsInnerFieldEnum>
> = {
  artist: 'parentRecording.bandArtistName.raw',
  title: 'parentRecording.recordingTitle.raw',
  release: 'earliestParentRelease.releaseTitle.raw',
  year: 'earliestParentRelease.dateReleased',
  format: 'earliestParentRelease.format',
  type: 'earliestParentRelease.mediaType',
  stageName: 'persona.stageName',
  live: 'parentRecording.isLive',
  contributions: 'westburyContributions.contribution',
  isrcs: 'parentRecording.isrcs',
  duration: 'parentRecording.duration',
  featured: 'performance.featuredStatus',
  collaborators: 'parentRecording.bandArtistName.raw',
  version: 'parentRecording.recordingVersion',
  // Note: version is not available in the API, so it will be sorted client-side only
};

// Map ag-grid column IDs to API filter field names
const GRID_COL_TO_API_FILTER_FIELD: Partial<Record<string, string>> = {
  artist: 'parentRecording.bandArtistName.raw',
  version: 'parentRecording.recordingVersion',
  contributions: 'westburyContributions.contribution',
  release: 'earliestParentRelease.releaseTitle.raw',
  year: 'earliestParentRelease.dateReleased',
  live: 'parentRecording.isLive',
  title: 'parentRecording.recordingTitle.raw',
  isrcs: 'parentRecording.isrcs',
  stageName: 'persona.stageName',
  type: 'earliestParentRelease.mediaType',
  format: 'earliestParentRelease.format',
  catNumbers: 'earliestParentRelease.catNumbers',
  label: 'earliestParentRelease.releaseLabels',
  featured: 'performance.featuredStatus',
};

// Default sort order: Format, Year of Release, Live, Release, Version, Recording Title, Artist
const DEFAULT_SORT_MODEL: SortModelItem[] = [
  // { colId: 'format', sort: 'asc' },
  { colId: 'year', sort: 'asc' },
  { colId: 'live', sort: 'asc' },
  { colId: 'release', sort: 'asc' },
  { colId: 'version', sort: 'asc' },
  { colId: 'title', sort: 'asc' },
  { colId: 'artist', sort: 'asc' },
];

interface Row {
  id: string;
  artist?: string | null;
  title: string;
  version?: string | null;
  contributions: RecordingWestburyContributionsInner[];
  isrcs: string;
  year?: string | null;
  release: string;
  duration?: string | null;
  isFeatured: boolean;
  isLive: boolean;
  // Subheadings (shown in expanded detail)
  collaborators: string;
  label: string;
  catNumbers: string;
  format: string;
  mediaType: string;
  stageName: string;
  location: string;
  source: string;
  labelsArray: string[];
  catNumbersArray: string[];
  versionsArray: string[];
  isrcsArray: string[];
  documentId?: string;
  earliestParentReleaseId?: string;
  earliestReleaseImageKey?: string;
  releaseImageKeys: string[];
  isModified?: boolean;
  originalValues?: Partial<Row>;
}

type GridRow =
  | (Row & {
      kind: 'data';
    })
  | {
      kind: 'detail';
      id: string;
      parentId: string;
      base: Row;
    };

type RowIdImageUrlMap = {
  [id: string]: string;
};

type RouteParams = {
  userId: string;
};

// Detail row data type
type DetailRowData = {
  artist: string;
  title: string;
  version: string;
  contributions: RecordingWestburyContributionsInner[];
  isrcs: string;
  release: string;
  year: string;
  duration: string;
  type: string;
  stageName: string;
  isLive: boolean;
  isFeatured: boolean;
  collaborators: string;
  label: string;
  catNumbers: string;
  format: string;
};

// Map DetailGrid field names to main grid column IDs
const DETAIL_FIELD_TO_MAIN_COL_ID: Partial<Record<string, string>> = {
  collaborators: 'artist', // Collaborators maps to artist column
  format: 'format',
  type: 'type',
  stageName: 'stageName',
  // label and catNumbers don't have corresponding main grid columns
};

// Combined Column Visibility Dropdown Component
const CombinedColumnVisibilityDropdown: React.FC<{
  gridApi: GridApi | null;
  columnDefs: ColDef[];
  detailColumnDefs: ColDef<DetailRowData>[];
  onDetailColumnVisibilityChange: (field: string, visible: boolean) => void;
  detailVisibleColumns: Set<string>;
}> = ({
  gridApi,
  columnDefs,
  detailColumnDefs,
  onDetailColumnVisibilityChange,
  detailVisibleColumns,
}) => {
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    // Initialize with all non-hidden columns
    const visible = new Set<string>();
    columnDefs.forEach((col) => {
      if (col.colId && !col.hide) {
        visible.add(col.colId);
      }
    });
    return visible;
  });

  // Update visible columns when columnDefs change
  useEffect(() => {
    if (!gridApi) return;
    const columnState = gridApi.getColumnState();
    const newVisible = new Set<string>();
    columnState.forEach((col) => {
      if (col.colId && !col.hide) {
        newVisible.add(col.colId);
      }
    });
    setVisibleColumns(newVisible);
  }, [gridApi, columnDefs]);

  const toggleMainColumnVisibility = (colId: string) => {
    if (!gridApi || !colId) return;

    const isVisible = visibleColumns.has(colId);
    const newVisible = new Set(visibleColumns);

    if (isVisible) {
      newVisible.delete(colId);
    } else {
      newVisible.add(colId);
    }

    setVisibleColumns(newVisible);
    gridApi.setColumnsVisible([colId], !isVisible);
  };

  const toggleDetailColumnVisibility = (field: string) => {
    const isVisible = detailVisibleColumns.has(field);
    onDetailColumnVisibilityChange(field, !isVisible);
  };

  // Filter out expand column and get displayable columns
  const displayableMainColumns = columnDefs.filter(
    (col) => col.colId && col.colId !== 'expand'
  );
  const displayableDetailColumns = detailColumnDefs.filter((col) => col.field);

  return (
    <WDropdown
      trigger={<WButton variant="secondary" label="Show/Hide Data" />}
      content={
        <div className="tw-min-w-[200px] tw-max-h-[500px] tw-overflow-y-auto">
          {/* Main Columns Section */}
          <div className="tw-px-2 tw-py-2 tw-text-xs tw-font-semibold tw-text-neutral-400 tw-uppercase tw-tracking-wide">
            Top-Level
          </div>
          {displayableMainColumns.map((col) => {
            const colId = col.colId!;
            const isVisible = visibleColumns.has(colId);
            return (
              <div
                key={`main-${colId}`}
                role="button"
                tabIndex={0}
                className="tw-px-2 tw-py-2 tw-cursor-pointer hover:tw-bg-neutral-800 tw-flex tw-items-center tw-gap-2"
                onClick={() => toggleMainColumnVisibility(colId)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleMainColumnVisibility(colId);
                  }
                }}
              >
                {isVisible ? (
                  <Eye className="tw-w-4 tw-h-4 tw-text-white" />
                ) : (
                  <EyeOff className="tw-w-4 tw-h-4 tw-text-neutral-500" />
                )}
                <span
                  className={
                    isVisible ? 'tw-text-white' : 'tw-text-neutral-500'
                  }
                >
                  {col.headerName || colId}
                </span>
              </div>
            );
          })}

          {/* Separator */}
          <div className="tw-px-2 tw-py-1 tw-border-t tw-border-neutral-700 tw-my-1" />

          {/* Detail Columns Section */}
          <div className="tw-px-2 tw-py-2 tw-text-xs tw-font-semibold tw-text-neutral-400 tw-uppercase tw-tracking-wide">
            Sub-Level
          </div>
          {displayableDetailColumns.map((col) => {
            const field = col.field!;
            const isVisible = detailVisibleColumns.has(field);
            return (
              <div
                key={`detail-${field}`}
                role="button"
                tabIndex={0}
                className="tw-px-2 tw-py-2 tw-cursor-pointer hover:tw-bg-neutral-800 tw-flex tw-items-center tw-gap-2"
                onClick={() => toggleDetailColumnVisibility(field)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleDetailColumnVisibility(field);
                  }
                }}
              >
                {isVisible ? (
                  <Eye className="tw-w-4 tw-h-4 tw-text-white" />
                ) : (
                  <EyeOff className="tw-w-4 tw-h-4 tw-text-neutral-500" />
                )}
                <span
                  className={
                    isVisible ? 'tw-text-white' : 'tw-text-neutral-500'
                  }
                >
                  {col.headerName || field}
                </span>
              </div>
            );
          })}
        </div>
      }
    />
  );
};

// Detail grid component
const DetailGrid: React.FC<{
  row: Row;
  onSortChange?: (colId: string, sort: SortDirection | null) => void;
  rowId: string;
  onGridReady?: (rowId: string, api: GridApi<DetailRowData>) => void;
  visibleColumns: Set<string>;
}> = ({ row, onSortChange, rowId, onGridReady, visibleColumns }) => {
  const detailGridApiRef = useRef<GridApi<DetailRowData> | null>(null);

  const detailColumnDefs = useMemo<ColDef<DetailRowData>[]>(
    () => [
      {
        headerName: 'Artist',
        hide: true,
        field: 'artist',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Recording Title',
        hide: true,
        field: 'title',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Version',
        hide: true,
        field: 'version',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Contributions',
        hide: true,
        field: 'contributions',
        minWidth: 150,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        cellRenderer: (params: ICellRendererParams<DetailRowData>) => {
          if (!params.data) {
            return null;
          }
          return (
            <div className="tw-flex tw-gap-2">
              {params.data.contributions.map((contribution) => (
                <ContributionPill
                  key={contribution.contribution}
                  contribution={contribution}
                />
              ))}
            </div>
          );
        },
      },
      {
        headerName: 'ISRCs',
        hide: true,
        field: 'isrcs',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Release',
        hide: true,
        field: 'release',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Year',
        hide: true,
        field: 'year',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Duration',
        hide: true,
        field: 'duration',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Live',
        hide: true,
        field: 'isLive',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },

      {
        headerName: 'Collaborators',
        field: 'collaborators',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Label',
        field: 'label',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Catalogue Number',
        field: 'catNumbers',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Format',
        field: 'format',
        minWidth: 100,
        sortable: true,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Type',
        field: 'type',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Stage Name',
        field: 'stageName',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Featured',
        field: 'isFeatured',
        minWidth: 100,
        sortable: false,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        cellRenderer: (params: ICellRendererParams<DetailRowData>) => {
          if (!params.data) {
            return null;
          }
          return (
            <div>
              <i
                className={clsx('wi wi-xs', {
                  'wi-check': params.data.isFeatured,
                  'wi-close': !params.data.isFeatured,
                })}
              />
            </div>
          );
        },
      },
    ],
    []
  );

  const detailRowData = useMemo<DetailRowData[]>(
    () => [
      {
        artist: row.artist || '—',
        title: row.title || '—',
        version: row.version || '—',
        contributions: row.contributions || '—',
        isrcs: row.isrcs || '—',
        release: row.release || '—',
        year: row.year || '—',
        duration: row.duration || '—',
        type: row.mediaType || '—',
        stageName: row.stageName || '—',
        isLive: row.isLive || false,
        isFeatured: row.isFeatured || false,
        collaborators: row.collaborators || '—',
        label: row.label || '—',
        catNumbers: row.catNumbers || '—',
        format: row.format || '—',
      },
    ],
    [row]
  );

  const detailDefaultColDef = useMemo<ColDef<DetailRowData>>(
    () => ({
      sortable: true,
      resizable: true,
      suppressMenu: true,
      suppressMovable: false,
      sortingOrder: ['asc', 'desc', null],
    }),
    []
  );

  const handleDetailSortChanged = useCallback(
    (event: SortChangedEvent<DetailRowData>) => {
      if (!event.api || !onSortChange) {
        return;
      }

      const sortedColumns = event.api
        .getColumnState()
        .filter(
          (col) =>
            col.sort && col.sortIndex !== null && col.sortIndex !== undefined
        )
        .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));

      // Get the primary sorted column (first one)
      const primaryColumn = sortedColumns[0];
      if (primaryColumn && primaryColumn.colId) {
        const mainColId = DETAIL_FIELD_TO_MAIN_COL_ID[primaryColumn.colId];
        if (mainColId && primaryColumn.sort) {
          // Trigger sort in main grid
          onSortChange(mainColId, primaryColumn.sort);
        }
      } else if (sortedColumns.length === 0) {
        // No sort - could reset to default, but for now do nothing
        // as DetailGrid sort shouldn't clear main grid sort
      }
    },
    [onSortChange]
  );

  const handleDetailGridReady = useCallback(
    (params: GridReadyEvent<DetailRowData>) => {
      detailGridApiRef.current = params.api;
      if (onGridReady) {
        onGridReady(rowId, params.api);
      }
      // Apply initial column visibility
      detailColumnDefs.forEach((colDef) => {
        if (colDef.field) {
          const shouldBeVisible = visibleColumns.has(colDef.field);
          params.api.setColumnsVisible([colDef.field], shouldBeVisible);
        }
      });
    },
    [onGridReady, rowId, detailColumnDefs, visibleColumns]
  );

  return (
    <div
      className="detail-grid-wrapper tw-w-full tw-h-full tw-rounded-b tw-p-3"
      style={{ width: '100%', height: '100%' }}
    >
      <div style={{ height: '60px', width: '100%', overflow: 'hidden' }}>
        <AgGridReact
          rowData={detailRowData}
          columnDefs={detailColumnDefs}
          defaultColDef={detailDefaultColDef}
          headerHeight={30}
          rowHeight={30}
          domLayout="normal"
          suppressHorizontalScroll={false}
          onSortChanged={handleDetailSortChanged}
          onGridReady={handleDetailGridReady}
          alwaysMultiSort={false}
          suppressRowHoverHighlight={true}
        />
      </div>
    </div>
  );
};

function MasterDiscography() {
  const { userId } = useParams<RouteParams>();
  const { adminApi: api } = useApi();
  const { throwError } = useWError();

  const [clientPersonalInfo, setClientPersonalInfo] =
    useState<AdminPersonalInformation>();
  const [isPendingExportGeneration, setIsPendingExportGeneration] =
    useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingError, setLoadingError] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [images, setImages] = useState<RowIdImageUrlMap>({});
  const [confidenceStats, setConfidenceStats] =
    useState<ConfidenceStats | null>(null);
  const [selectedConfidenceCategory, setSelectedConfidenceCategory] = useState<
    string | null
  >(null);
  const [availableFilters, setAvailableFilters] =
    useState<AvailableFiltersResponse | null>(null);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');

  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(50);
  const [recordingsCount, setRecordingsCount] = useState<number>(0);

  const [isOpenExportConfirmationModal, setIsOpenExportConfirmationModal] =
    useState<boolean>(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [selectedRowForModal, setSelectedRowForModal] = useState<Row | null>(
    null
  );

  const mainContentColumnRef = useRef<HTMLDivElement>(null);
  const editFormRef = useRef<RecordingEditFormHandle>(null);
  const [panelBounds, setPanelBounds] = useState<{
    top: number;
    height: number;
  } | null>(null);

  const sortFieldsRef = useRef<PrimaryDiscographyRequestSortFieldsInner[]>([]);
  const [sortFields, setSortFields] = useState<
    PrimaryDiscographyRequestSortFieldsInner[]
  >(() => {
    // Build default sortFields from DEFAULT_SORT_MODEL
    const defaultSortFields = DEFAULT_SORT_MODEL.map((item) => {
      const apiField = GRID_SORT_TO_API_FIELD[item.colId];
      if (apiField && item.sort) {
        return {
          field: apiField,
          order: item.sort === 'desc' ? 'desc' : 'asc',
        };
      }
      return null;
    }).filter(
      (item): item is PrimaryDiscographyRequestSortFieldsInner => item !== null
    );
    sortFieldsRef.current = defaultSortFields;
    return defaultSortFields;
  });
  const [fieldFilters, setFieldFilters] = useState<
    PrimaryDiscographyRequestFieldFiltersInner[]
  >([]);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(
    () => new Set()
  );
  const gridApiRef = useRef<GridApi<GridRow> | null>(null);
  const prevDebouncedSearchTermRef = useRef<string>('');
  const isInitialMountRef = useRef<boolean>(true);
  const isApplyingInitialSortRef = useRef<boolean>(false);

  // Serialize arrays for stable dependency comparison
  const sortFieldsKey = useMemo(() => JSON.stringify(sortFields), [sortFields]);
  const fieldFiltersKey = useMemo(
    () => JSON.stringify(fieldFilters),
    [fieldFilters]
  );

  // Track detail grid APIs for column visibility control
  const detailGridApiRefs = useRef<Map<string, GridApi<DetailRowData>>>(
    new Map()
  );

  // Measure main content column so the side panel height matches the table area
  useEffect(() => {
    if (
      !showEditPanel ||
      !selectedRowForModal ||
      !mainContentColumnRef.current
    ) {
      setPanelBounds(null);
      return;
    }
    const el = mainContentColumnRef.current;
    const updateBounds = () => {
      const rect = el.getBoundingClientRect();
      setPanelBounds({ top: rect.top, height: rect.height });
    };
    updateBounds();
    const ro = new ResizeObserver(updateBounds);
    ro.observe(el);
    return () => ro.disconnect();
  }, [showEditPanel, selectedRowForModal]);

  // Detail grid column visibility state - initialized from detailColumnDefs
  const detailColumnDefsForVisibility = useMemo<ColDef<DetailRowData>[]>(
    () => [
      {
        headerName: 'Artist',
        hide: true,
        field: 'artist',
      },
      {
        headerName: 'Recording Title',
        hide: true,
        field: 'title',
      },
      {
        headerName: 'Version',
        hide: true,
        field: 'version',
      },
      {
        headerName: 'Contributions',
        hide: true,
        field: 'contributions',
      },
      {
        headerName: 'ISRCs',
        hide: true,
        field: 'isrcs',
      },
      {
        headerName: 'Release',
        hide: true,
        field: 'release',
      },
      {
        headerName: 'Year',
        hide: true,
        field: 'year',
      },
      {
        headerName: 'Duration',
        hide: true,
        field: 'duration',
      },
      {
        headerName: 'Type',
        hide: true,
        colId: 'type',
      },
      {
        headerName: 'Stage Name',
        hide: true,
        field: 'stageName',
      },
      {
        headerName: 'Live',
        hide: true,
        field: 'isLive',
      },
      {
        headerName: 'Featured',
        field: 'isFeatured',
      },
      {
        headerName: 'Collaborators',
        field: 'collaborators',
      },
      {
        headerName: 'Label',
        field: 'label',
      },
      {
        headerName: 'Catalogue Number',
        field: 'catNumbers',
      },
      {
        headerName: 'Format',
        field: 'format',
      },
      {
        headerName: 'Type',
        field: 'type',
      },
    ],
    []
  );

  const [detailVisibleColumns, setDetailVisibleColumns] = useState<Set<string>>(
    () => {
      const visible = new Set<string>();
      // Initialize with all non-hidden detail columns
      detailColumnDefsForVisibility.forEach((col) => {
        if (col.field && !col.hide) {
          visible.add(col.field);
        }
      });
      return visible;
    }
  );

  const initialColumnState = useMemo<ColumnState[]>(
    () =>
      DEFAULT_SORT_MODEL.filter(
        (item): item is Required<Pick<SortModelItem, 'colId' | 'sort'>> =>
          Boolean(item.colId && item.sort)
      ).map((item, index) => ({
        colId: item.colId,
        sort: item.sort as SortDirection,
        sortIndex: index,
      })),
    []
  );

  const defaultSortStateByColId = useMemo(
    () =>
      new Map<string, { sort: SortDirection; sortIndex: number }>(
        initialColumnState
          .filter(
            (
              col
            ): col is ColumnState & {
              sort: SortDirection;
              sortIndex: number;
            } =>
              col.sort !== null &&
              col.sortIndex !== null &&
              col.sort !== undefined &&
              col.sortIndex !== undefined
          )
          .map(({ colId, sort, sortIndex }) => [colId, { sort, sortIndex }])
      ),
    [initialColumnState]
  );

  const skipToLastPage = () => {
    const totalPages = Math.ceil(recordingsCount / rowsPerPage);
    if (totalPages > 0) {
      setPage(totalPages);
    }
  };

  const handleSortChanged = useCallback((event: SortChangedEvent<GridRow>) => {
    if (!event.api) {
      return;
    }

    // Ignore sort changes during initial sort application
    if (isApplyingInitialSortRef.current) {
      return;
    }

    // Get the current sort state from the grid
    const sortedColumns = event.api
      .getColumnState()
      .filter(
        (col) =>
          col.sort && col.sortIndex !== null && col.sortIndex !== undefined
      )
      .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));

    // Build sortFields array from sorted columns
    const newSortFields: PrimaryDiscographyRequestSortFieldsInner[] =
      sortedColumns
        .map((col) => {
          if (!col.colId) return null;
          const apiField = GRID_SORT_TO_API_FIELD[col.colId];
          if (!apiField || !col.sort) return null;
          return {
            field: apiField,
            order: col.sort === 'desc' ? 'desc' : 'asc',
          };
        })
        .filter(
          (item): item is PrimaryDiscographyRequestSortFieldsInner =>
            item !== null
        );

    // Compare sort fields (order matters for multi-sort)
    const sortChanged =
      JSON.stringify(sortFieldsRef.current) !== JSON.stringify(newSortFields);

    // Only update if sort actually changed
    if (!sortChanged) {
      return;
    }

    // Update sortFields with new sort (can be empty array if user removed all sorts)
    sortFieldsRef.current = newSortFields;
    setSortFields(newSortFields);
    setPage(1);
  }, []);

  const handleFilterChanged = useCallback(
    (event: FilterChangedEvent<GridRow>) => {
      if (!event.api) {
        return;
      }

      const filterModel = event.api.getFilterModel();
      const newFieldFilters: PrimaryDiscographyRequestFieldFiltersInner[] = [];

      // Convert AG Grid filter model to API fieldFilters format
      // Note: filterModel only contains active filters, so if a filter is cleared, it won't be in the model
      Object.keys(filterModel).forEach((colId) => {
        const apiField = GRID_COL_TO_API_FILTER_FIELD[colId];
        if (!apiField) {
          return; // Skip columns that don't have filter mapping
        }

        const filter = filterModel[colId];
        if (!filter) {
          return; // Skip if filter is null/undefined
        }

        // Handle checkbox filter (AgCheckboxFilter returns { values: string[] })
        if (filter && filter.values && Array.isArray(filter.values)) {
          // Only add filter if at least one value is selected (filter is active)
          // When no values are selected, the filter is inactive and we don't filter
          if (filter.values.length > 0) {
            // Filter out the BLANK_KEY if present, as it's not a real value
            const realValues = filter.values.filter(
              (v: string) => v !== '__AG_CHECKBOX_FILTER_BLANK__'
            );
            if (realValues.length > 0) {
              // Special handling for featured column: expand "Yes" to ["featured", "otherFeatured"]
              let expandedValues = realValues;
              if (colId === 'featured') {
                expandedValues = realValues.flatMap((v: string) => {
                  if (v === 'Yes') {
                    return ['featured', 'otherFeatured'];
                  }
                  return [v];
                });
              }
              const filterConfig: PrimaryDiscographyRequestFieldFiltersInner = {
                field: apiField,
                value: expandedValues,
              };
              if (colId === 'featured') {
                filterConfig.caseSensitive = true;
              }
              newFieldFilters.push(filterConfig);
            }
          }
          // If filter.values.length === 0, filter is inactive, so we don't add it to fieldFilters
        } else if (filter && filter.filter !== undefined) {
          // AG Grid text filter stores the filter value in the 'filter' property
          const filterValue = String(filter.filter).trim();
          // Only add filter if there's a non-empty value
          if (filterValue) {
            newFieldFilters.push({
              field: apiField,
              value: [filterValue],
            });
          }
          // If filterValue is empty, don't add to fieldFilters (filter is cleared)
        }
      });

      // Compare with existing filters to prevent unnecessary updates
      const newFieldFiltersKey = JSON.stringify(newFieldFilters);
      const currentFieldFiltersKey = JSON.stringify(fieldFilters);

      // Only update if filters actually changed
      if (newFieldFiltersKey !== currentFieldFiltersKey) {
        setFieldFilters(newFieldFilters);
        setPage(1);
      }
    },
    [fieldFilters]
  );

  // Helper function to get available values for a column from API filters
  const getAvailableValuesForColumn = useCallback(
    (colId: string): string[] | null => {
      if (!availableFilters) {
        return null;
      }

      const apiField = GRID_COL_TO_API_FILTER_FIELD[colId];
      if (!apiField) {
        return null;
      }

      const filterData = availableFilters.availableFilters.find(
        (f) => f.field === apiField
      );
      return filterData?.values ?? null;
    },
    [availableFilters]
  );

  const handleGridReady = useCallback(
    (params: GridReadyEvent<GridRow>) => {
      gridApiRef.current = params.api;

      // Only apply initial sort state on first mount
      if (isInitialMountRef.current) {
        isApplyingInitialSortRef.current = true;
        params.api.applyColumnState({
          defaultState: { sort: null, sortIndex: null },
          state: initialColumnState,
        });
        isApplyingInitialSortRef.current = false;
        isInitialMountRef.current = false;
      }
    },
    [initialColumnState]
  );

  const handleDetailGridSortChange = useCallback(
    (colId: string, sort: SortDirection | null) => {
      if (!gridApiRef.current) {
        return;
      }

      // Apply the sort to this column as primary sort
      // First, clear all sorts
      gridApiRef.current.applyColumnState({
        defaultState: { sort: null, sortIndex: null },
      });

      // Then apply the new sort
      if (sort) {
        gridApiRef.current.applyColumnState({
          state: [
            {
              colId,
              sort,
              sortIndex: 0,
            },
          ],
        });
      }
      // handleSortChanged will be triggered automatically and update sortFields
    },
    []
  );

  const handleDetailColumnVisibilityChange = useCallback(
    (field: string, visible: boolean) => {
      setDetailVisibleColumns((prev) => {
        const newSet = new Set(prev);
        if (visible) {
          newSet.add(field);
        } else {
          newSet.delete(field);
        }
        return newSet;
      });

      // Apply to all detail grids
      detailGridApiRefs.current.forEach((api) => {
        api.setColumnsVisible([field], visible);
      });
    },
    []
  );

  const resetSortOrder = useCallback(() => {
    if (!gridApiRef.current) {
      return;
    }

    gridApiRef.current.applyColumnState({
      defaultState: { sort: null, sortIndex: null },
      state: initialColumnState,
    });

    // handleSortChanged will be triggered automatically and update sortFields
    // But we also need to update sortFields immediately for consistency
    const defaultSortFields = DEFAULT_SORT_MODEL.map((item) => {
      const apiField = GRID_SORT_TO_API_FIELD[item.colId];
      if (apiField && item.sort) {
        return {
          field: apiField,
          order: item.sort === 'desc' ? 'desc' : 'asc',
        };
      }
      return null;
    }).filter(
      (item): item is PrimaryDiscographyRequestSortFieldsInner => item !== null
    );

    sortFieldsRef.current = defaultSortFields;
    setSortFields(defaultSortFields);
    setPage(1);
  }, [initialColumnState]);

  const resetFilters = useCallback(() => {
    if (!gridApiRef.current) {
      return;
    }

    // Clear all filters from the grid
    gridApiRef.current.setFilterModel(null);

    // Reset field filters state
    setFieldFilters([]);
    setPage(1);
  }, []);

  const toggleRowExpansion = useCallback((rowId: string) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  }, []);

  const expandAllRows = useCallback(() => {
    setExpandedRowIds(new Set(rows.map((row) => row.id)));
  }, [rows]);

  const collapseAllRows = useCallback(() => {
    setExpandedRowIds(new Set());
  }, []);

  const hasAnyExpandedRows = useMemo(() => {
    return expandedRowIds.size > 0;
  }, [expandedRowIds.size]);

  const handleOpenEditModal = useCallback((row: Row) => {
    setSelectedRowForModal(row);
    setShowEditPanel(true);
  }, []);

  const handleSaveModalEdit = useCallback((updatedRow: Row) => {
    setRows((prevRows) => {
      return prevRows.map((row) => {
        if (row.id === updatedRow.id) {
          return {
            ...row,
            ...updatedRow,
            isModified: true,
          };
        }
        return row;
      });
    });
    setShowEditModal(false);
    setSelectedRowForModal(null);
  }, []);

  const gridRows = useMemo<GridRow[]>(() => {
    const expanded = new Set(expandedRowIds);
    const result: GridRow[] = [];

    rows.forEach((row) => {
      result.push({ ...row, kind: 'data' });
      if (expanded.has(row.id)) {
        result.push({
          kind: 'detail',
          id: `${row.id}__detail`,
          parentId: row.id,
          base: row,
        });
      }
    });

    return result;
  }, [expandedRowIds, rows]);

  const getRowId = useCallback((params: { data: GridRow | undefined }) => {
    if (!params.data) {
      return '';
    }
    if (params.data.kind === 'detail') {
      return params.data.id;
    }
    return params.data.id;
  }, []);

  const getRowHeight = useCallback((params: RowHeightParams<GridRow>) => {
    if (!params.data) {
      return 48;
    }

    if (params.data.kind === 'detail') {
      // Header height (30) + row height (70) + padding (24) = 124
      return 94;
    }

    return 48;
  }, []);

  const handlePostSortRows = useCallback(
    ({ nodes }: PostSortRowsParams<GridRow>) => {
      const detailNodes: typeof nodes = [];

      for (let i = nodes.length - 1; i >= 0; i--) {
        const node = nodes[i];
        if (node.data?.kind === 'detail') {
          detailNodes.push(node);
          nodes.splice(i, 1);
        }
      }

      detailNodes.reverse().forEach((detailNode) => {
        const data = detailNode.data;
        if (!data || data.kind !== 'detail') {
          nodes.push(detailNode);
          return;
        }

        const parentId = data.parentId;
        if (!parentId) {
          nodes.push(detailNode);
          return;
        }

        const parentIndex = nodes.findIndex(
          (node) => node.data?.kind === 'data' && node.data.id === parentId
        );

        if (parentIndex === -1) {
          nodes.push(detailNode);
          return;
        }

        nodes.splice(parentIndex + 1, 0, detailNode);
      });
    },
    []
  );

  useEffect(() => {
    setExpandedRowIds((prev) => {
      const available = new Set(rows.map((row) => row.id));
      const retained = new Set<string>();
      prev.forEach((id) => {
        if (available.has(id)) {
          retained.add(id);
        }
      });
      return retained.size === prev.size ? prev : retained;
    });
  }, [rows]);

  // Reset row heights when confidence filter changes and new data arrives
  // This fixes the visual bug where rows render with wrong heights after filter change
  const prevConfidenceCategoryRef = useRef<string | null>(null);
  useEffect(() => {
    // Check if confidence category changed
    const confidenceChanged =
      prevConfidenceCategoryRef.current !== selectedConfidenceCategory;

    if (confidenceChanged && gridApiRef.current && rows.length > 0) {
      // Reset row heights after confidence filter change to fix visual bug
      // Use requestAnimationFrame to ensure this happens after React render
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (gridApiRef.current) {
            gridApiRef.current.resetRowHeights();
          }
        });
      });
    }

    prevConfidenceCategoryRef.current = selectedConfidenceCategory;
  }, [selectedConfidenceCategory, rows.length]);

  const fetchData = async () => {
    if (userId === undefined) {
      return;
    }

    setIsLoading(true);

    api.retrieveAdminPersonalInformation({ userId }).then((personalInfo) => {
      setClientPersonalInfo(personalInfo);
    });

    try {
      const discographyData = await api.searchPrimaryDiscography({
        userId: userId,
        primaryDiscographyRequest: {
          size: rowsPerPage,
          fromOffset: (page - 1) * rowsPerPage,
          sortFields,
          ...(sortFields.length > 0 && {
            sortField: sortFields[0].field,
            sortOrder: sortFields[0].order,
          }),
          confidenceCategory: selectedConfidenceCategory ?? undefined,
          searchTerm: debouncedSearchTerm || undefined,
          fieldFilters: fieldFilters.length > 0 ? fieldFilters : undefined,
        },
      });

      const rows = discographyData.recordings.map((recording) => {
        const {
          parentRecording,
          earliestParentRelease,
          evidenceReleases,
          westburyContributions,
          performance,
          persona,
        } = recording;
        return {
          id: parentRecording.id,
          artist: parentRecording.bandArtistName,
          title: parentRecording.recordingTitle,
          version: (parentRecording.recordingVersion ?? []).join(', '), // Version not available in API, set as null
          release: earliestParentRelease?.releaseTitle ?? '',
          year: earliestParentRelease?.dateReleased,
          isrcs: (parentRecording.isrcs ?? []).join(', '),
          contributions: westburyContributions.sort((a, b) =>
            b.confidenceCategory.localeCompare(a.confidenceCategory)
          ),
          duration: parentRecording.duration ?? null,
          isFeatured: ['featured', 'otherFeatured'].includes(
            performance.featuredStatus ?? ''
          ),
          isLive: parentRecording.live,
          // Subheadings data
          collaborators: '', // Do not remove the invisible space charater " "
          catNumbers: (earliestParentRelease?.catNumbers ?? []).join(', '),
          label: (earliestParentRelease?.releaseLabels ?? []).join(', '),
          format: earliestParentRelease?.format ?? '',
          mediaType: earliestParentRelease?.mediaType ?? '',
          stageName: persona.stageName,
          location: parentRecording.countryOfRecording ?? '',
          source: recording.parentRecording.source.toString(),
          labelsArray: earliestParentRelease?.releaseLabels ?? [],
          catNumbersArray: earliestParentRelease?.catNumbers ?? [],
          versionsArray: parentRecording.recordingVersion ?? [],
          isrcsArray: parentRecording.isrcs ?? [],
          documentId: recording.documentId ?? undefined,
          earliestParentReleaseId: earliestParentRelease?.id,
          // notes: '',
          earliestReleaseImageKey: (
            evidenceReleases?.at(0)?.evidenceImages ?? []
          )
            .map((image) => image.objectKey)
            .at(0),
          releaseImageKeys: (evidenceReleases ?? []).flatMap((release) =>
            (release.evidenceImages ?? []).map((image) => image.objectKey)
          ),
        };
      });

      // Merge with existing rows to preserve editing state
      setRows((prevRows) => {
        return rows.map((newRow) => {
          const existingRow = prevRows.find((r) => r.id === newRow.id);
          if (existingRow && existingRow.isModified) {
            return {
              ...newRow,
              ...existingRow,
              // Keep modified values but update other fields
              artist: existingRow.artist,
              title: existingRow.title,
              release: existingRow.release,
              year: existingRow.year,
              isrcs: existingRow.isrcs,
            };
          }
          return {
            ...newRow,
            isModified: false,
            originalValues: {},
          };
        });
      });
      setRecordingsCount(discographyData.count);
    } catch (e) {
      console.error(e);
      setLoadingError(true);
    }

    setIsLoading(false);
  };

  const transformConfidenceStatsToChartData = (stats: ConfidenceStats) => {
    const getCategoryIcon = (categoryId: string): React.ReactNode => {
      switch (categoryId) {
        case '1':
          return <CircleCheck className="tw-w-5 tw-h-5" />;
        case '2':
          return <InfoIcon className="tw-w-5 tw-h-5" />;
        case '3':
          return (
            <div className="tw-p-1 tw-rounded-full tw-border-[#f97316] tw-border-[2px] tw-w-5 tw-h-5">
              <Flag className="tw-w-2.5 tw-h-2.5 -tw-mt-[1px] -tw-ml-[1px]" />
            </div>
          );
        case '4':
          return <HelpCircleIcon className="tw-w-5 tw-h-5" />;
        case '5':
          return <CircleX className="tw-w-5 tw-h-5" />;
        default:
          return <HelpCircleIcon className="tw-w-5 tw-h-5" />;
      }
    };

    const getCategoryInfo = (categoryId: string) => {
      switch (categoryId) {
        case '1':
          return {
            label: 'Verified (No Effort)',
            text: 'black',
            color: '#6EE038',
            description:
              'Contributions that have been verified and require no additional effort',
          };
        case '2':
          return {
            label: 'Inferred (Low Effort)',
            text: 'black',
            color: '#F6FF00',
            description:
              'Contributions that can be inferred with low verification effort',
          };
        case '3':
          return {
            label: 'Credited (Mid Effort)',
            text: 'white',
            color: '#FF8A00',
            description:
              'Contributions that are credited and require moderate verification effort',
          };
        case '4':
          return {
            label: 'Unverified (High Effort)',
            text: 'white',
            color: '#FF2600',
            description:
              'Contributions that are unverified and require high verification effort',
          };
        case '5':
          return {
            label: 'Unknown (Max Effort)',
            text: 'black',
            color: '#AAB2B9',
            description:
              'Contributions with unknown status requiring maximum verification effort',
          };
        default:
          return {
            label: `Category ${categoryId}`,
            text: 'white',
            color: '#AAB2B9',
            description: 'Unknown confidence category',
          };
      }
    };

    stats.confidenceCategories.sort(
      (a, b) => Number(a.confidenceCategory) - Number(b.confidenceCategory)
    );

    return stats.confidenceCategories.map((category) => {
      const categoryInfo = getCategoryInfo(category.confidenceCategory);
      return {
        id: category.confidenceCategory,
        label: categoryInfo.label,
        value: category.count,
        text: categoryInfo.text,
        color: categoryInfo.color,
        icon: getCategoryIcon(category.confidenceCategory),
        description: categoryInfo.description,
      };
    });
  };

  const fetchConfidenceStats = async () => {
    if (userId === undefined) {
      return;
    }

    try {
      const stats = await api.confidenceStatsConfidenceStats({ userId });
      setConfidenceStats(stats);
    } catch (e) {
      console.error('Failed to fetch confidence stats:', e);
      // Set to null on error so we can show default data or handle gracefully
      setConfidenceStats(null);
    }
  };

  const fetchAvailableFilters = async () => {
    if (userId === undefined) {
      return;
    }

    try {
      const filters = await api.availableFiltersAvailableFiltersResponse({
        userId,
      });
      setAvailableFilters(filters);
    } catch (e) {
      console.error('Failed to fetch available filters:', e);
      setAvailableFilters(null);
    }
  };

  const handleConfidenceFilterChange = (categoryId: string | null) => {
    setSelectedConfidenceCategory(categoryId);
    setPage(1); // Reset to first page when filter changes
    // Don't clear rows immediately - let fetchData handle it naturally
    // This prevents AG Grid from having stale row nodes with cached heights
    setIsLoading(true); // Set loading state
  };

  useEffect(() => {
    fetchData();
  }, [
    userId,
    page,
    rowsPerPage,
    sortFieldsKey,
    selectedConfidenceCategory,
    debouncedSearchTerm,
    fieldFiltersKey,
  ]);

  useEffect(() => {
    fetchConfidenceStats();
    fetchAvailableFilters();
  }, [userId]);

  // Debounce search term with 300ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Reset to page 1 when debounced search term changes (but not on initial mount)
  useEffect(() => {
    if (prevDebouncedSearchTermRef.current !== debouncedSearchTerm) {
      if (!isInitialMountRef.current) {
        setPage(1);
      }
      prevDebouncedSearchTermRef.current = debouncedSearchTerm;
      isInitialMountRef.current = false;
    }
  }, [debouncedSearchTerm]);

  // Update filter available values when availableFilters changes
  useEffect(() => {
    if (!gridApiRef.current || !availableFilters) {
      return;
    }

    // Update each filterable column's filter with new available values
    const filterableColumns = [
      'artist',
      'version',
      'contributions',
      'release',
      'year',
      'live',
      'stageName',
      'type',
      'format',
      'label',
      'catNumbers',
      'featured',
    ];
    filterableColumns.forEach((colId) => {
      const column = gridApiRef.current?.getColumn(colId);
      if (!column) {
        return;
      }

      // Use getColumnApi or direct access to get filter instance
      const filterInstance = (gridApiRef.current as any).getFilterInstance?.(
        colId
      );
      if (
        filterInstance &&
        typeof filterInstance.setAvailableValues === 'function'
      ) {
        const availableValues = getAvailableValuesForColumn(colId);
        if (availableValues) {
          filterInstance.setAvailableValues(availableValues);
        }
      }
    });
  }, [availableFilters, getAvailableValuesForColumn]);

  const fetchSignedEvidenceImageUrls = () => {
    if (userId === undefined) {
      return;
    }
    for (const row of rows) {
      if (row.earliestReleaseImageKey === undefined) {
        continue;
      }
      api
        .evidenceImageSignedUrlEvidenceImageSignedUrl({
          userId,
          evidenceImageSignedUrlRequest: {
            objectKey: row.earliestReleaseImageKey,
          },
        })
        .then(({ signedUrl }) => {
          setImages((prev) => {
            return { ...prev, [row.id]: signedUrl };
          });
        })
        .catch((e) => {
          console.error(e);
        });
    }
  };

  useEffect(() => {
    fetchSignedEvidenceImageUrls();
  }, [rows]);

  const fetchLatestExportStatus = async () => {
    if (userId === undefined) {
      return;
    }
    api
      .retrieveLatestPrimaryDiscographyExport({ userId })
      .then((primaryDiscographyExport) => {
        setIsPendingExportGeneration(!primaryDiscographyExport.ready);
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchLatestExportStatus();
  }, [userId]);

  const handleGenerateExport = () => {
    if (userId === undefined) {
      return;
    }
    api
      .createGeneratePrimaryDiscographyExport({ userId })
      .then(() => {
        setIsOpenExportConfirmationModal(true);
        fetchLatestExportStatus();
      })
      .catch(() => {
        throwError(new WError('Discography export generation request failed'));
      });
  };

  const defaultColDef = useMemo<ColDef<GridRow>>(
    () => ({
      sortable: true,
      resizable: true,
      suppressMenu: true,
      suppressMovable: false,
      sortingOrder: ['asc', 'desc', null],
      flex: 1,
      minWidth: 80,
    }),
    []
  );

  // Store images in a ref so cell renderers can access it without causing columnDefs to recreate
  const imagesRef = useRef<RowIdImageUrlMap>(images);
  const prevImagesKeysRef = useRef<string>(JSON.stringify(Object.keys(images)));
  useEffect(() => {
    const currentImagesKeys = JSON.stringify(Object.keys(images));
    imagesRef.current = images;

    // Refresh cells when images change (including initial load)
    // Check if images actually changed to avoid unnecessary refreshes
    if (gridApiRef.current && currentImagesKeys !== prevImagesKeysRef.current) {
      prevImagesKeysRef.current = currentImagesKeys;
      // Use setTimeout to ensure this happens after render
      setTimeout(() => {
        if (gridApiRef.current) {
          gridApiRef.current.refreshCells({
            columns: ['evidence'],
            force: true,
          });
        }
      }, 0);
    }
  }, [images]);

  // Store expandedRowIds in a ref so cell renderers can access it without causing columnDefs to recreate
  const expandedRowIdsRef = useRef<Set<string>>(expandedRowIds);
  useEffect(() => {
    expandedRowIdsRef.current = expandedRowIds;
  }, [expandedRowIds]);

  // Custom header component that shows sort index next to sort icon
  // Uses AG Grid's native sort handling via setSort method
  // Defined outside useMemo to prevent recreation
  const CustomHeaderWithSortIndex: React.FC<IHeaderParams<GridRow>> = (
    params
  ) => {
    // Use state to force re-render when sort changes
    const [sortState, setSortState] = useState<{
      sort: SortDirection;
      sortIndex: number | null | undefined;
    }>(() => ({
      sort: params.column?.getSort() ?? null,
      sortIndex: params.column?.getSortIndex() ?? null,
    }));

    // Track filter state
    const [isFiltered, setIsFiltered] = useState<boolean>(() => {
      if (!params.column) return false;
      const filterModel = params.api.getFilterModel();
      const colId = params.column.getColId();
      const filter = filterModel[colId];

      // Check for text filter
      if (filter && filter.filter) {
        return true;
      }

      // Check for checkbox filter (AgCheckboxFilter returns { values: string[] })
      if (
        filter &&
        filter.values &&
        Array.isArray(filter.values) &&
        filter.values.length > 0
      ) {
        return true;
      }

      return false;
    });

    // Track menu open state to properly toggle (use ref to avoid re-renders in event handler)
    const menuOpenRef = useRef<boolean>(false);
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

    // Listen to sort changes and update state to force re-render
    useEffect(() => {
      const updateSortState = () => {
        setSortState({
          sort: params.column?.getSort() ?? null,
          sortIndex: params.column?.getSortIndex() ?? null,
        });
      };

      const updateFilterState = () => {
        if (!params.column) return;
        const filterModel = params.api.getFilterModel();
        const colId = params.column.getColId();
        const filter = filterModel[colId];

        // Check for text filter
        let hasFilter = false;
        if (filter && filter.filter) {
          hasFilter = true;
        } else if (
          filter &&
          filter.values &&
          Array.isArray(filter.values) &&
          filter.values.length > 0
        ) {
          // Check for checkbox filter with selected values
          hasFilter = true;
        }

        setIsFiltered(hasFilter);
      };

      // Update filter state when filter changes (don't update menu state here to avoid loops)
      // AG Grid automatically closes the menu when filters are applied
      const handleFilterChanged = () => {
        updateFilterState();
        // Update ref without causing re-render
        menuOpenRef.current = false;
      };

      // Update immediately
      updateSortState();
      updateFilterState();

      // Listen to sort changed events
      params.api.addEventListener('sortChanged', updateSortState);
      // Listen to filter changed events (only update filter state, not menu state)
      params.api.addEventListener('filterChanged', handleFilterChanged);

      return () => {
        params.api.removeEventListener('sortChanged', updateSortState);
        params.api.removeEventListener('filterChanged', handleFilterChanged);
      };
    }, [params.api, params.column]);

    const sortIndex = sortState.sortIndex;
    const sort = sortState.sort;
    const displayName = params.displayName;

    // Handle sort click - use AG Grid's native setSort method
    const onSortRequested = (
      event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
    ) => {
      if (!params.enableSorting || !params.column) {
        return;
      }

      // Check for Ctrl (Windows/Linux) or Cmd (Mac) for multi-sort
      // The grid is configured with multiSortKey="ctrl"
      const mouseEvent = event as React.MouseEvent<HTMLDivElement>;
      const multiSortKey = mouseEvent.ctrlKey || mouseEvent.metaKey;

      // Use AG Grid's native setSort method which handles multi-sort automatically
      // The multiSortKey="ctrl" setting will be respected
      params.setSort(
        sort === 'asc' ? 'desc' : sort === 'desc' ? null : 'asc',
        multiSortKey
      );
    };

    // Handle filter icon click - toggle filter menu
    const onFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();
      if (!params.column) {
        return;
      }
      // If menu is open, close it; otherwise open it
      // Use ref to check state to avoid stale closures
      if (menuOpenRef.current) {
        // Close the menu
        params.api.hidePopupMenu();
        menuOpenRef.current = false;
        setIsMenuOpen(false);
      } else {
        // Open the column menu which contains the filter
        params.showColumnMenu(event.currentTarget);
        menuOpenRef.current = true;
        setIsMenuOpen(true);
      }
    };

    return (
      <div
        className={`ag-header-cell-label tw-flex tw-items-center tw-gap-1 tw-w-full ${
          params.enableSorting ? 'tw-cursor-pointer' : ''
        }`}
        onClick={params.enableSorting ? onSortRequested : undefined}
        onTouchEnd={params.enableSorting ? onSortRequested : undefined}
        role={params.enableSorting ? 'button' : undefined}
        tabIndex={params.enableSorting ? 0 : undefined}
        onKeyDown={
          params.enableSorting
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSortRequested(e as any);
                }
              }
            : undefined
        }
        aria-label={params.enableSorting ? `Sort by ${displayName}` : undefined}
      >
        <span className="tw-flex-1">{displayName}</span>
        <div className="tw-flex tw-items-center tw-gap-1">
          {/* Filter icon - show if column has filter enabled */}
          {params.column?.getColDef().filter && (
            <button
              type="button"
              onClick={onFilterClick}
              className={`tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-rounded tw-transition-colors ${
                isFiltered
                  ? 'tw-bg-blue-600 tw-text-white tw-p-1'
                  : 'tw-bg-transparent tw-text-neutral-500 tw-p-1 hover:tw-bg-neutral-800'
              }`}
              aria-label={`Filter ${displayName}`}
            >
              <Filter
                className={`tw-w-3 tw-h-3 ${
                  isFiltered ? 'tw-text-white' : 'tw-text-neutral-500'
                }`}
              />
            </button>
          )}
          {/* Sort controls - show if sorting is enabled */}
          {params.enableSorting && (
            <>
              {sort && sortIndex !== null && sortIndex !== undefined && (
                <span className="tw-text-xs tw-text-neutral-400 tw-font-medium tw-h-[14px]">
                  {sortIndex + 1}
                </span>
              )}
              {sort ? (
                <div className="tw-flex tw-items-center">
                  {sort === 'asc' ? (
                    <ArrowUp className="tw-w-3 tw-h-3" />
                  ) : (
                    <ArrowDown className="tw-w-3 tw-h-3" />
                  )}
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    );
  };

  const ExpandAllHeaderComponent: React.FC<IHeaderParams<GridRow>> = () => {
    return null;
  };

  const columnDefs = useMemo<ColDef<GridRow>[]>(() => {
    return [
      {
        headerName: '',
        colId: 'expand',
        width: 20,
        maxWidth: 20,
        cellStyle: (params) => {
          const baseStyle = {
            padding: 0,
            display: 'flex',
          };
          if (params.data?.kind === 'detail') {
            return {
              ...baseStyle,
              alignItems: 'stretch',
              justifyContent: 'stretch',
              width: '100%',
            };
          }
          return {
            ...baseStyle,
            alignItems: 'center',
            justifyContent: 'center',
          };
        },
        headerClass: 'tw-flex tw-items-center tw-justify-center',
        flex: 0,
        sortable: false,
        resizable: false,
        suppressMovable: true,
        headerComponent: ExpandAllHeaderComponent,
        cellRenderer: (params: ICellRendererParams<GridRow>) => {
          if (!params.data) {
            return null;
          }

          if (params.data.kind === 'detail') {
            const row = params.data.base;
            return (
              <DetailGrid
                row={row}
                onSortChange={handleDetailGridSortChange}
                rowId={params.data.parentId}
                onGridReady={(rowId, api) => {
                  detailGridApiRefs.current.set(rowId, api);
                }}
                visibleColumns={detailVisibleColumns}
              />
            );
          }

          const isExpanded = expandedRowIdsRef.current.has(params.data.id);

          return (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                toggleRowExpansion(params.data!.id);
              }}
              className="tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded tw-border tw-border-[#30353b] tw-bg-transparent tw-text-white hover:tw-border-[#4a5056]"
              aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
            >
              {isExpanded ? (
                <ChevronDown className="tw-h-4 tw-w-4" />
              ) : (
                <ChevronRight className="tw-h-4 tw-w-4" />
              )}
            </button>
          );
        },
        colSpan: (params: ColSpanParams<GridRow>) => {
          if (params.data?.kind === 'detail') {
            return Math.max(params.api.getAllDisplayedColumns().length, 1);
          }
          return 1;
        },
      },
      {
        headerName: 'Evidence',
        colId: 'evidence',
        width: 80,
        minWidth: 100,
        flex: 0,
        sortable: false,
        cellRenderer: (params: ICellRendererParams<GridRow>) => {
          if (!params.data || params.data.kind !== 'data') {
            return null;
          }

          const row = params.data;
          const imageUrl = imagesRef.current[row.id];
          const hasImageKey = !!row.earliestReleaseImageKey;

          return (
            <div className="tw-w-[45px] tw-h-[45px]">
              {hasImageKey ? (
                <Link to={`/discography/${userId}/ai-evidence/${row.id}`}>
                  <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-cursor-pointer">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt="Evidence"
                        className="tw-w-full tw-h-full"
                      />
                    ) : (
                      <div className="tw-flex tw-justify-center">
                        <p className="tw-my-auto tw-animate-ping">⚪</p>
                      </div>
                    )}
                  </div>
                </Link>
              ) : (
                <div className="tw-w-full tw-h-full tw-flex tw-justify-center tw-items-center">
                  <i className="wi wi-shut-eye wi-20px" />
                </div>
              )}
            </div>
          );
        },
        colSpan: (params: ColSpanParams<GridRow>) => {
          if (params.data?.kind === 'detail') {
            return 0;
          }
          return 1;
        },
      },
      {
        headerName: 'Artist',
        colId: 'artist',
        field: 'artist',
        width: 160,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.artist : '',
        filterValueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.artist : null,
        filterParams: {
          availableValues: getAvailableValuesForColumn('artist') ?? undefined,
          serverSideFiltering: true,
        },
      },
      {
        headerName: 'Recording Title',
        colId: 'title',
        field: 'title',
        width: 128,
        minWidth: 200,
        sortable: true,
        filter: AgTextFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.title : '',
        filterParams: {
          serverSideFiltering: true,
        },
      },
      {
        headerName: 'Version',
        hide: false,
        colId: 'version',
        field: 'version',
        width: 50,
        minWidth: 150,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.version || '-' : '',
        filterValueGetter: (params) => {
          if (params.data?.kind !== 'data') {
            return null;
          }
          return params.data.versionsArray?.length ? params.data.versionsArray : null;
        },
        filterParams: {
          availableValues: getAvailableValuesForColumn('version') ?? undefined,
          serverSideFiltering: true,
        },
        cellRenderer: (params: ICellRendererParams<GridRow>) => {
          if (params.data?.kind !== 'data') {
            return null;
          }
          return <span>{params.data.version || '-'}</span>;
        },
      },
      {
        headerName: 'Contributions',
        colId: 'contributions',
        field: 'contributions',
        minWidth: 150,
        width: 150,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.contributions : [],
        filterValueGetter: (params) => {
          if (params.data?.kind !== 'data') {
            return null;
          }
          // Return as array for better filtering - split comma-separated values
          const contributions = params.data.contributions;
          if (!contributions) {
            return null;
          }
          // Split by comma and trim each value
          return contributions.map((contribution) => contribution.contribution);
        },
        filterParams: {
          availableValues:
            getAvailableValuesForColumn('contributions') ?? undefined,
          serverSideFiltering: true,
        },
        cellRenderer: (params: ICellRendererParams<GridRow>) => {
          if (params.data?.kind !== 'data') {
            return null;
          }
          return (
            <div className="tw-flex tw-gap-2">
              {params.data.contributions.map((contribution) => (
                <ContributionPill
                  key={contribution.contribution}
                  contribution={contribution}
                />
              ))}
            </div>
          );
        },
      },
      {
        headerName: 'ISRCs',
        colId: 'isrcs',
        field: 'isrcs',
        width: 50,
        sortable: true,
        filter: AgTextFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.isrcs : '',
        filterParams: {
          serverSideFiltering: true,
        },
      },
      {
        headerName: 'Release',
        colId: 'release',
        field: 'release',
        width: 50,
        minWidth: 160,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.release : '',
        filterValueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.release : null,
        filterParams: {
          availableValues: getAvailableValuesForColumn('release') ?? undefined,
          serverSideFiltering: true,
        },
      },
      {
        headerName: 'Year',
        colId: 'year',
        field: 'year',
        width: 80,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) => {
          if (params.data?.kind !== 'data' || !params.data.year) {
            return '';
          }
          return new Date(params.data.year).getFullYear().toString();
        },
        filterValueGetter: (params) => {
          if (params.data?.kind !== 'data' || !params.data.year) {
            return null;
          }
          return new Date(params.data.year).getFullYear().toString();
        },
        filterParams: {
          availableValues: getAvailableValuesForColumn('year') ?? undefined,
          serverSideFiltering: true,
          // Extract year from date strings like "1999-01-23"
          valueExtractor: (apiValue: string) => {
            try {
              const date = new Date(apiValue);
              if (!isNaN(date.getTime())) {
                return date.getFullYear().toString();
              }
            } catch {
              // If parsing fails, return as-is
            }
            return apiValue;
          },
        },
      },
      {
        headerName: 'Duration',
        colId: 'duration',
        field: 'duration',
        width: 100,
        sortable: true,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.duration || '-' : '',
        cellRenderer: (params: ICellRendererParams<GridRow>) => {
          if (params.data?.kind !== 'data') {
            return null;
          }
          return <span>{params.data.duration || '-'}</span>;
        },
      },
      {
        headerName: 'Type',
        hide: true,
        colId: 'type',
        field: 'mediaType',
        width: 120,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.mediaType : '',
        filterValueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.mediaType : null,
        filterParams: {
          availableValues: getAvailableValuesForColumn('type') ?? undefined,
          serverSideFiltering: true,
        },
      },
      {
        headerName: 'Stage Name',
        hide: true,
        colId: 'stageName',
        field: 'stageName',
        width: 150,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.stageName : '',
        filterValueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.stageName : null,
        filterParams: {
          availableValues:
            getAvailableValuesForColumn('stageName') ?? undefined,
          serverSideFiltering: true,
        },
      },
      {
        headerName: 'Live',
        colId: 'live',
        field: 'isLive',
        width: 90,
        maxWidth: 120,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.isLive : '',
        filterValueGetter: (params) =>
          params.data?.kind === 'data'
            ? params.data.isLive
              ? 'Yes'
              : 'No'
            : null,
        filterParams: {
          availableValues: getAvailableValuesForColumn('live') ?? undefined,
          serverSideFiltering: true,
          // Map API values "True"/"False" to display values "Yes"/"No"
          valueMapper: (apiValue: string) => {
            if (apiValue === 'True') return 'Yes';
            if (apiValue === 'False') return 'No';
            return apiValue;
          },
          // Map display values "Yes"/"No" back to API values "True"/"False"
          reverseMapper: (displayValue: string) => {
            if (displayValue === 'Yes') return 'True';
            if (displayValue === 'No') return 'False';
            return displayValue;
          },
        },
        cellRenderer: (params: ICellRendererParams<GridRow>) => {
          if (params.data?.kind !== 'data') {
            return null;
          }
          return (
            <div>
              <i
                className={clsx('wi wi-xs', {
                  'wi-check': params.data.isLive,
                  'wi-close': !params.data.isLive,
                })}
              />
            </div>
          );
        },
      },
      {
        headerName: 'Featured',
        hide: true,
        colId: 'featured',
        field: 'isFeatured',
        width: 140,
        maxWidth: 140,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.isFeatured : '',
        filterValueGetter: (params) => {
          if (params.data?.kind !== 'data') {
            return null;
          }
          // Map boolean to display value
          return params.data.isFeatured ? 'Yes' : 'No';
        },
        filterParams: {
          availableValues: getAvailableValuesForColumn('featured') ?? undefined,
          serverSideFiltering: true,
          caseSensitive: true,
          // Map API values to display values
          valueMapper: (apiValue: string) => {
            if (apiValue === 'featured' || apiValue === 'otherFeatured')
              return 'Yes';
            if (apiValue === 'nonFeatured') return 'No';
            return apiValue;
          },
          // Map display values back to API values (will be expanded in handleFilterChanged)
          reverseMapper: (displayValue: string) => {
            // Keep "Yes" as-is, it will be expanded to ["featured", "otherFeatured"] in handleFilterChanged
            if (displayValue === 'Yes') return 'Yes';
            if (displayValue === 'No') return 'nonFeatured';
            return displayValue;
          },
        },
        cellRenderer: (params: ICellRendererParams<GridRow>) => {
          if (params.data?.kind !== 'data') {
            return null;
          }
          return (
            <div>
              <i
                className={clsx('wi wi-xs', {
                  'wi-check': params.data.isFeatured,
                  'wi-close': !params.data.isFeatured,
                })}
              />
            </div>
          );
        },
      },
      {
        headerName: 'Format',
        hide: true,
        colId: 'format',
        field: 'format',
        width: 120,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.format : '',
        filterValueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.format : null,
        filterParams: {
          availableValues: getAvailableValuesForColumn('format') ?? undefined,
          serverSideFiltering: true,
          caseInsensitiveDeduplication: true,
        },
      },
      {
        headerName: 'Collaborators',
        colId: 'collaborators',
        field: 'collaborators',
        hide: true,
        minWidth: 100,
        sortable: true,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
      },
      {
        headerName: 'Label',
        colId: 'label',
        field: 'label',
        hide: true,
        minWidth: 100,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.label : '',
        filterValueGetter: (params) => {
          if (params.data?.kind !== 'data') {
            return null;
          }
          return params.data.labelsArray?.length ? params.data.labelsArray : null;
        },
        filterParams: {
          availableValues: getAvailableValuesForColumn('label') ?? undefined,
          serverSideFiltering: true,
        },
      },
      {
        headerName: 'Catalogue Number',
        colId: 'catNumbers',
        field: 'catNumbers',
        hide: true,
        minWidth: 100,
        sortable: true,
        filter: AgCheckboxFilter,
        suppressMenu: false,
        headerComponent: CustomHeaderWithSortIndex,
        cellClass:
          'tw-text-textDove tw-flex tw-items-center tw-text-white tw-font-light',
        valueGetter: (params) =>
          params.data?.kind === 'data' ? params.data.catNumbers : '',
        filterValueGetter: (params) => {
          if (params.data?.kind !== 'data') {
            return null;
          }
          return params.data.catNumbersArray?.length ? params.data.catNumbersArray : null;
        },
        filterParams: {
          availableValues:
            getAvailableValuesForColumn('catNumbers') ?? undefined,
          serverSideFiltering: true,
        },
      },
      {
        headerName: 'Actions',
        colId: 'actions',
        width: 120,
        minWidth: 120,
        maxWidth: 120,
        flex: 0,
        sortable: false,
        resizable: false,
        cellClass: 'tw-flex tw-items-center tw-justify-center',
        cellRenderer: (params: ICellRendererParams<GridRow>) => {
          if (!params.data || params.data.kind !== 'data') {
            return null;
          }
          const row = params.data;
          return (
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
              <WButton
                icon="edit"
                variant="secondary"
                label="Edit"
                onClick={() => handleOpenEditModal(row)}
              />
            </div>
          );
        },
      },
    ];
  }, [
    userId,
    toggleRowExpansion,
    handleDetailGridSortChange,
    getAvailableValuesForColumn,
    handleOpenEditModal,
  ]);

  // Highlight row currently being edited, and visually indicate modified rows
  const getRowStyle = useCallback(
    (params: RowClassParams<GridRow>): RowStyle | undefined => {
      if (params.data?.kind !== 'data') return undefined;
      const row = params.data;
      const isBeingEdited =
        selectedRowForModal != null && row.id === selectedRowForModal.id;
      if (isBeingEdited) {
        return { backgroundColor: '#262626' } as RowStyle;
      }
      if (row.isModified) {
        return {
          backgroundColor: 'rgba(255, 165, 0, 0.1)',
          boxShadow: 'inset 3px 0 0 0 #FFA500',
        } as RowStyle;
      }
      return undefined;
    },
    [selectedRowForModal]
  );

  return (
    <PageLayout>
      <EditRecordingModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedRowForModal(null);
        }}
        row={selectedRowForModal}
        onSave={handleSaveModalEdit}
        userId={userId}
        availableFormats={allReleaseTypes}
        availableTypes={allReleaseFormats}
        availableContributions={allContributionRoles}
        availableArtists={getAvailableValuesForColumn('artist') ?? []}
      />
      <ExportConfirmationModal
        isOpen={isOpenExportConfirmationModal}
        onClose={() => setIsOpenExportConfirmationModal(false)}
      />
      <div
        className={`tw-flex tw-w-full tw-gap-4 ${
          showEditPanel && selectedRowForModal
            ? 'tw-mr-[calc(min(520px,95vw)+1rem)]'
            : ''
        }`}
      >
        <div
          ref={mainContentColumnRef}
          className={clsx(
            'tw-flex-1 tw-min-w-0 tw-flex tw-flex-col tw-gap-6',
            showEditPanel &&
              selectedRowForModal &&
              'tw-pr-[calc(min(520px,95vw)+1rem)]'
          )}
        >
          {/* Back Button */}
          <div className="tw-flex tw-mb-6">
            <WButton
              variant="link-secondary"
              icon="chevron-left"
              onClick={() => {
                window.history.back();
              }}
              label="Back"
            />
          </div>
          <div className="tw-flex tw-flex-col tw-gap-2 tw-w-full">
            <div className="tw-flex tw-flex-col tw-justify-between tw-w-full">
              <div>
                <h2>{`${clientPersonalInfo?.firstName} ${clientPersonalInfo?.lastName}`}</h2>
              </div>
              <div className="tw-flex-grow tw-flex tw-items-end tw-gap-4">
                <VerificationChart
                  data={
                    confidenceStats
                      ? transformConfidenceStatsToChartData(confidenceStats)
                      : []
                  }
                  onFilterChange={handleConfidenceFilterChange}
                />
                <div className="tw-justify-self-end tw-mb-[18px]">
                  <WButton
                    variant="secondary"
                    label="Export Discography"
                    icon="export"
                    disabled={isPendingExportGeneration}
                    onClick={handleGenerateExport}
                  />
                </div>
              </div>
            </div>
            <div className="tw-bg-surfaceBlack90 tw-p-6 tw-text-white tw-rounded-2xl tw-max-w-full IS_W_CONTAINER">
              <div className="tw-flex tw-justify-between tw-items-center">
                <div className="tw-flex tw-flex-col tw-gap-2 tw-w-1/2">
                  <div className="tw-relative">
                    <div className="tw-relative">
                      <i
                        className={`wi wi-20px wi-search tw-absolute tw-left-3 tw-top-[14px]`}
                      ></i>
                      <input
                        className={clsx(
                          'tw-bg-formInputBackground',
                          'tw-text-gray-400',
                          'tw-rounded-lg',
                          'tw-w-full',
                          'tw-h-12',
                          'tw-placeholder-textSeal',
                          'tw-text-sm',
                          'tw-leading-5',
                          'tw-p-3',
                          'tw-pl-9',
                          'tw-border-2',
                          'tw-border-formInputBackground',
                          'focus:tw-border-white',
                          'focus:tw-text-white',
                          'focus:tw-outline-none'
                        )}
                        id="discography-search"
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search discography..."
                        autoComplete="off"
                      />
                    </div>
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => setSearchTerm('')}
                        className="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-text-neutral-400 hover:tw-text-white tw-transition-colors"
                        aria-label="Clear search"
                      >
                        <X className="tw-w-4 tw-h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="tw-flex tw-gap-2">
                  <WButton
                    variant="secondary"
                    label="Expand/Collapse All"
                    onClick={() => {
                      if (hasAnyExpandedRows) {
                        collapseAllRows();
                      } else {
                        expandAllRows();
                      }
                    }}
                  />
                  <CombinedColumnVisibilityDropdown
                    gridApi={gridApiRef.current}
                    columnDefs={columnDefs}
                    detailColumnDefs={detailColumnDefsForVisibility}
                    onDetailColumnVisibilityChange={
                      handleDetailColumnVisibilityChange
                    }
                    detailVisibleColumns={detailVisibleColumns}
                  />
                  <WButton
                    variant="secondary"
                    label="Reset Sort"
                    onClick={resetSortOrder}
                  />
                  <WButton
                    variant="secondary"
                    label="Reset Filters"
                    onClick={resetFilters}
                  />
                </div>
              </div>
              <div>
                <div className="tw-h-[500px]">
                  <AgGridReact
                    theme={myTheme}
                    rowData={gridRows}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    pagination={false}
                    onSortChanged={handleSortChanged}
                    onFilterChanged={handleFilterChanged}
                    onGridReady={handleGridReady}
                    postSortRows={handlePostSortRows}
                    getRowId={getRowId}
                    getRowHeight={getRowHeight}
                    getRowStyle={getRowStyle}
                    masterDetail={true}
                    animateRows={true}
                    domLayout="normal"
                    multiSortKey="ctrl"
                    loading={isLoading && rows.length === 0}
                    isExternalFilterPresent={() => {
                      // Tell AG Grid that filtering is handled server-side
                      // Check if any filters are active (fieldFilters, searchTerm, or confidenceCategory)
                      return (
                        fieldFilters.length > 0 ||
                        debouncedSearchTerm.length > 0 ||
                        selectedConfidenceCategory !== null
                      );
                    }}
                    doesExternalFilterPass={() => {
                      // Since filtering is handled server-side, always return true
                      // The backend has already filtered the data, so we show all rows returned
                      return true;
                    }}
                    overlayNoRowsTemplate={
                      loadingError
                        ? '<div class="ag-overlay-no-rows-center" style="color: white;"><h5>Error loading data</h5></div>'
                        : debouncedSearchTerm || selectedConfidenceCategory
                        ? '<div class="ag-overlay-no-rows-center" style="color: white;"><h5>The filters did not match any data</h5></div>'
                        : '<div class="ag-overlay-no-rows-center" style="color: white;"><h5>No data available currently</h5></div>'
                    }
                  />
                </div>
                {recordingsCount > 0 && (
                  <DataTablePagination
                    currentPage={page}
                    totalRows={recordingsCount}
                    rowsPerPage={rowsPerPage}
                    onPageChange={(newPage) => {
                      setPage(newPage);
                    }}
                    onRowsPerPageChange={(newRowsPerPage) => {
                      setRowsPerPage(newRowsPerPage);
                      setPage(1);
                    }}
                    onGoToLastPage={skipToLastPage}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        {showEditPanel && selectedRowForModal && (
          <aside
            className="tw-fixed tw-right-0 tw-w-[min(520px,95vw)] tw-border-l tw-border-gray-600 tw-bg-surfaceBlack90 tw-flex tw-flex-col tw-overflow-hidden tw-rounded-l-2xl tw-z-10"
            style={{
              boxShadow: '-4px 0 12px rgba(0,0,0,0.15)',
              ...(panelBounds
                ? { top: panelBounds.top, height: panelBounds.height }
                : { top: 64, height: 'calc(100vh - 124px)' }),
            }}
          >
            <div className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-border-b tw-border-gray-600 tw-flex-shrink-0">
              <h3 className="tw-text-white tw-text-base tw-font-semibold">
                Edit Recording
              </h3>
              <div className="tw-flex tw-gap-2">
                <WButton
                  variant="secondary"
                  label="Full screen"
                  onClick={() => setShowEditModal(true)}
                />
                <WButton
                  variant="secondary"
                  label="Close"
                  onClick={() => {
                    setShowEditPanel(false);
                    setSelectedRowForModal(null);
                  }}
                />
              </div>
            </div>
            <div className="tw-flex-1 tw-overflow-y-auto tw-min-h-0">
              <RecordingEditForm
                ref={editFormRef}
                row={selectedRowForModal}
                onSave={(r) => {
                  handleSaveModalEdit(r);
                  setShowEditPanel(false);
                  setSelectedRowForModal(null);
                }}
                onCancel={() => {
                  setShowEditPanel(false);
                  setSelectedRowForModal(null);
                }}
                stacked
                hideFooter
                userId={userId}
                availableFormats={allReleaseTypes}
                availableTypes={allReleaseFormats}
                availableContributions={allContributionRoles}
                availableArtists={getAvailableValuesForColumn('artist') ?? []}
              />
            </div>
          </aside>
        )}
      </div>
    </PageLayout>
  );
}

interface EditRecordingModalProps {
  isOpen: boolean;
  onClose: () => void;
  row: Row | null;
  onSave: (row: Row) => void;
  userId?: string;
  availableFormats?: string[];
  availableTypes?: string[];
  availableContributions?: string[];
  availableArtists?: string[];
}

const EditRecordingModal: React.FC<EditRecordingModalProps> = ({
  isOpen,
  onClose,
  row,
  onSave,
  userId,
  availableFormats = [],
  availableTypes = [],
  availableContributions = [],
  availableArtists = [],
}) => {
  if (!isOpen || !row) return null;
  return (
    <WModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Recording Details"
      modalClasses="tw-max-w-5xl tw-w-full tw-max-h-[90vh] tw-flex tw-flex-col tw-bg-gray-900 tw-overflow-hidden tw-mx-4"
    >
      <div className="tw-flex-1 tw-overflow-hidden tw-flex tw-flex-col tw-min-h-0">
        <RecordingEditForm
          row={row}
          onSave={(r) => {
            onSave(r);
            onClose();
          }}
          onCancel={onClose}
          userId={userId}
          availableFormats={availableFormats}
          availableTypes={availableTypes}
          availableContributions={availableContributions}
          availableArtists={availableArtists}
        />
      </div>
    </WModal>
  );
};

interface ExportConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ExportConfirmationModal: React.FC<ExportConfirmationModalProps> = ({
  isOpen,
  onClose,
}) => {
  return (
    <WModal isOpen={isOpen} onClose={onClose} title="" hasCloseButton={true}>
      <div className="tw-pb-8">
        <ThankyouBlock
          title="Discography Export In Progress"
          description="Once complete, a download link will appear as a task on your
          dashboard."
        />
      </div>
    </WModal>
  );
};

interface DataTablePaginationProps {
  currentPage: number;
  totalRows: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onGoToLastPage: () => void;
}

const DataTablePagination: React.FC<DataTablePaginationProps> = ({
  currentPage,
  totalRows,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onGoToLastPage,
}) => {
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const startRow = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endRow = Math.min(currentPage * rowsPerPage, totalRows);
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage >= totalPages;

  // Calculate which page numbers to show
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage <= 3) {
        // Show first few pages
        for (let i = 2; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('ellipsis');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Show last few pages
        pages.push('ellipsis');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show pages around current
        pages.push('ellipsis');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('ellipsis');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="tw-flex tw-items-center tw-justify-between tw-mt-4 tw-text-white tw-text-sm">
      <div className="tw-flex tw-items-center tw-gap-2">
        <span className="tw-text-neutral-400">Rows per page:</span>
        <select
          value={rowsPerPage}
          onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
          className="tw-bg-[#151719] tw-border tw-border-[#535D66] tw-rounded tw-px-2 tw-py-1 tw-text-white focus:tw-outline-none focus:tw-border-[#4a5056]"
        >
          {[25, 50, 100, 500].map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>
      <div className="tw-flex tw-items-center tw-gap-4">
        <span className="tw-text-neutral-400 tw-text-sm">
          {startRow}-{endRow} of {totalRows}
        </span>
        <Pagination className="[&&]:tw-w-auto">
          <PaginationContent>
            <PaginationItem>
              <PaginationLink
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (!isFirstPage) {
                    onPageChange(1);
                  }
                }}
                className={
                  isFirstPage
                    ? 'tw-pointer-events-none tw-opacity-50'
                    : 'tw-cursor-pointer tw-gap-1 tw-px-2.5'
                }
                size="default"
              >
                <ChevronsLeft className="tw-h-4 tw-w-4" />
                <span className="tw-hidden sm:tw-inline-block">First</span>
              </PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (!isFirstPage) {
                    onPageChange(currentPage - 1);
                  }
                }}
                className={
                  isFirstPage
                    ? 'tw-pointer-events-none tw-opacity-50'
                    : 'tw-cursor-pointer'
                }
              />
            </PaginationItem>
            {pageNumbers.map((pageNum, index) => {
              if (pageNum === 'ellipsis') {
                return (
                  <PaginationItem key={`ellipsis-${index}`}>
                    <span className="tw-px-2 tw-text-neutral-400">...</span>
                  </PaginationItem>
                );
              }
              const pageNumber = pageNum as number;
              const isActive = pageNumber === currentPage;
              return (
                <PaginationItem key={pageNumber}>
                  <PaginationLink
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (!isActive) {
                        onPageChange(pageNumber);
                      }
                    }}
                    style={{
                      color: isActive ? 'black' : 'white',
                    }}
                    className={
                      isActive
                        ? 'tw-bg-white tw-text-black tw-pointer-events-none'
                        : 'tw-cursor-pointer'
                    }
                    size="default"
                  >
                    {pageNumber}
                  </PaginationLink>
                </PaginationItem>
              );
            })}
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (!isLastPage) {
                    onPageChange(currentPage + 1);
                  }
                }}
                className={
                  isLastPage
                    ? 'tw-pointer-events-none tw-opacity-50'
                    : 'tw-cursor-pointer'
                }
              />
            </PaginationItem>
            <PaginationItem>
              <PaginationLink
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (!isLastPage) {
                    onGoToLastPage();
                  }
                }}
                className={
                  isLastPage
                    ? 'tw-pointer-events-none tw-opacity-50'
                    : 'tw-cursor-pointer tw-gap-1 tw-px-2.5'
                }
                size="default"
              >
                <span className="tw-hidden sm:tw-inline-block">Last</span>
                <ChevronsRight className="tw-h-4 tw-w-4" />
              </PaginationLink>
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  );
};

export default MasterDiscography;
